import React, { FocusEventHandler, MouseEventHandler } from "react";
// @ts-ignore
import "../../static/css/autocomplete.css";
import { getMapboxGeocodeUrlQuery } from "./LocationSelectModal";

/**
 * Component prop types for ReactMapboxAutocomplete.
 */
type ComponentTypes = {
    inputId?: string,
    inputOnFocus?: FocusEventHandler<HTMLInputElement>,
    inputOnBlur?: FocusEventHandler<HTMLInputElement>,
    inputOnClick?: MouseEventHandler<HTMLInputElement>,
    inputClass?: string,
    publicKey: string,
    placeholder: string,
    onSuggestionSelect: ( result: string, lat: string, lng: string, valid: boolean ) => void,
    country: string,
    query: string,
    resetSearch: boolean,
    setIsInteractingWithTextFieldLocationInput: React.Dispatch<React.SetStateAction<boolean>>, //used to disable drag when the user is interacting with the text field.
    backgroundColor: string,
};

class ReactMapboxAutocomplete extends React.Component<ComponentTypes, any> // eslint-disable-line @typescript-eslint/no-explicit-any
{
    touchMoved = false;
    touchStartY = 0;
    state = {
        error: false,
        errorMsg: "",
        query: this.props.query ? this.props.query : "",
        queryResults: [],
        publicKey: this.props.publicKey,
        resetSearch: this.props.resetSearch ? this.props.resetSearch : false,
        // State to show/hide suggestions for autocomplete
        suggestions: true,
        valid: false,
    };

    componentDidMount()
    {
    // Clean up the initial query and update state
        const cleanedUpQuery = this.getCleanedUpName( this.state.query );
        this.setState( { query: cleanedUpQuery } );
    }

    /**
   * Fetch geocoding data from Mapbox API based on the query.
   * Invalidate
   * @param {string} query - The search query.
   */
    _fetchGeocodingData = ( query: string ) =>
    {
        const header = { "Content-Type": "application/json" };
        const path = getMapboxGeocodeUrlQuery( query, true );

        if ( query.length > 2 )
        {
            return fetch( path, { headers: header } )
                .then( ( res ) =>
                {
                    if ( !res.ok ) throw Error( res.statusText );
                    return res.json();
                } )
                .then( ( json ) =>
                {
                    this.setState( {
                        error: false,
                        queryResults: json.features,
                    } );
                } )
                .catch( () =>
                {
                    this.setState( {
                        error: true,
                        errorMsg: "There was a problem retrieving data from Mapbox",
                        queryResults: [],
                    } );
                } );
        }
        else
        {
            this.setState( {
                error: false,
                queryResults: [],
            } );
        }
    };

    /**
   * Update the query state and fetch new geocoding data.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event.
   */
    _updateQuery = ( event: { target: { value: string } } ) =>
    {
        this.props.onSuggestionSelect( "", "", "", false );
        const query = event.target.value;
        this.setState( { query } );
        void this._fetchGeocodingData( query );
        this.props.setIsInteractingWithTextFieldLocationInput( true );
    };

    /**
   * Handle paste event and fetch new geocoding data.
   * @param {React.ClipboardEvent<HTMLInputElement>} event - The paste event.
   */
    _onPaste = ( event: React.ClipboardEvent<HTMLInputElement> ) =>
    {
    // Invalidate the input on paste but allow the dropdown to work
        this.props.onSuggestionSelect( "", "", "", false );
        this.state.valid = false;
        const pastedData = event.clipboardData.getData( "text" );
        void this._fetchGeocodingData( this.getCleanedUpName( pastedData ) );
        this.props.setIsInteractingWithTextFieldLocationInput( true );
    };

    /**
   * Clean up the name by removing country and zip code.
   * @param {string} name - The name to be cleaned up.
   * @returns {string} - The cleaned-up name.
   */
    getCleanedUpName = ( name: string ) =>
    {
        //let cleanedUpName = name.replace( ", United States", "" ); // Remove country
        return name.replace( /\d{5}$/, "" ); // Remove zip code
    };

    /**
   * Handle the selection of a suggestion from the autocomplete dropdown.
   * This is the only way to get a valid input!
   * @param {React.MouseEvent<HTMLElement>} event - The click event.
   */
    _onSuggestionSelect = ( event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement> ) =>
    {
        if ( event.target === null ) return;

        const name = ( event.target as HTMLElement ).getAttribute( "data-suggestion" );
        const cleanedUpName = name !== null ? this.getCleanedUpName( name ) : "";
        if ( !this.state.resetSearch )
        {
            this.setState( { query: cleanedUpName } );
        }
        this.props.onSuggestionSelect(
            cleanedUpName,
            ( event.target as HTMLElement ).getAttribute( "data-lat" ) ?? "",
            ( event.target as HTMLElement ).getAttribute( "data-lng" ) ?? "",
            true
        );
        this.state.valid = true;
        this.props.setIsInteractingWithTextFieldLocationInput( false );
    };

    /**
   * Reset the search input and results.
   */
    _resetSearch = () =>
    {
        if ( this.state.resetSearch )
        {
            this.setState( {
                query: "",
                queryResults: [],
            } );
        }
        else
        {
            this.setState( { queryResults: [] } );
        }
    };

    render()
    {
        return (
            <div>
                <input
                    placeholder = {this.props.placeholder || "Search"}
                    id = {this.props.inputId}
                    onClick = {( e ) =>
                    {
                        ( e.target as HTMLInputElement ).select();
                        if ( this.props.inputOnClick ) this.props.inputOnClick( e );
                        this.setState( { suggestions: true } );
                    }}
                    style = {{ backgroundColor: this.props.backgroundColor, fontSize: "16px" }}
                    onBlur = {( e ) =>
                    {
                        //do this if it is not currently valid (last field in onSuggestionSelect)
                        if ( !this.state.valid )
                        {
                            this.setState( {
                                query: "",
                                queryResults: [],
                            } );
                        }
                        if ( this.props.inputOnBlur ) this.props.inputOnBlur( e );
                        this.setState( { suggestions: false } );
                    }}
                    onFocus = {this.props.inputOnFocus}
                    className = {
                        this.props.inputClass !== undefined
                            ? this.props.inputClass + " react-mapbox-ac-input"
                            : "react-mapbox-ac-input"
                    }
                    onChange = {this._updateQuery}
                    onPaste = {this._onPaste}
                    value = {this.state.query}
                    type = "text"
                />
                <span>
                    <div
                        className = "react-mapbox-ac-menu"
                        style = {
                            ( this.state.queryResults.length > 0 || this.state.error ) &&
              this.state.suggestions
                                ? { display: "block" }
                                : { display: "none" }
                        }
                        onClick = {this._resetSearch}
                    >
                        {this.state.queryResults.map(
                            (
                                place: {
                                    place_name:
                                    | string
                                    | number
                                    | boolean
                                    | React.ReactElement<
                                    any, // eslint-disable-line @typescript-eslint/no-explicit-any
                                    string | React.JSXElementConstructor<any>// eslint-disable-line @typescript-eslint/no-explicit-any
                                    >
                                    | React.ReactFragment
                                    | React.ReactPortal
                                    | null
                                    | undefined,
                                    center: Array<any>, // eslint-disable-line @typescript-eslint/no-explicit-any
                                    text: any, // eslint-disable-line @typescript-eslint/no-explicit-any
                                },
                                i: React.Key | null | undefined
                            ) =>
                            {
                                return (
                                    <div
                                        className = "react-mapbox-ac-suggestion"
                                        // must use onMouseDown event
                                        // onClick happens after onBlur from the textfield (which would cause onSuggestionSelect to never get called)
                                        onMouseDown = {this._onSuggestionSelect}
                                        onTouchStart = {( e ) =>
                                        {
                                            this.touchStartY = e.touches[0].clientY;
                                            this.touchMoved = false;
                                        }}
                                        onTouchMove = {( e ) =>
                                        {
                                            const deltaY = Math.abs( e.touches[0].clientY - this.touchStartY );
                                            if ( deltaY > 5 )
                                            {
                                                this.touchMoved = true;
                                            }
                                        }}
                                        onTouchEnd = {( e ) =>
                                        {
                                            if ( !this.touchMoved )
                                            {
                                                this._onSuggestionSelect( e );
                                            }
                                        }}
                                        key = {i}
                                        data-suggestion = {place.place_name}
                                        data-lng = {place.center[0]}
                                        data-lat = {place.center[1]}
                                        data-text = {place.text}
                                    >
                                        {place.place_name}
                                    </div>
                                );
                            }
                        )}


                        {this.state.error && (
                            <div className = "react-mapbox-ac-suggestion">
                                {this.state.errorMsg}
                            </div>
                        )}
                    </div>
                </span>
            </div>
        );
    }
}

export default ReactMapboxAutocomplete;
